import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Euro, DollarSign, PoundSterling } from 'lucide-react';
import { format } from 'date-fns';
//import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { jwtDecode } from "jwt-decode";
import { getLocale } from "@/i18n/config";
import { faro } from "@/faro";
import i18n from 'i18next';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const getCurrentLanguage = (): string => {
  return i18n.language || 'en'; // Fallback to 'en' if no language is set
};

export const formatDateTime = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  // Format the date and time in 'yyyy-MM-dd'T'HH:mm' format
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

export const formatDate = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  // Use the `format` function from `date-fns` to format the date
  return format(date, 'P', { locale: getLocale() }); // 'P' will format the date as month/day/year (localized)
};

export const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  // Use the `format` function from `date-fns` to format the time
  return format(date, 'p', { locale: getLocale() }); // 'p' will format the time in a short format (localized)
};

export const formatDay = (dateString: string): string => {
  const date = new Date(dateString);
  return format(date, 'EEEE', { locale: getLocale() });
};

type CurrencyIconProps = {
  currencyCode: string;
  size?: number; // optional size in pixels
};

export const getCurrencyIcon = ({ currencyCode, size = 5 }: CurrencyIconProps) => {
  const iconClass = `inline-block w-${size} h-${size} mr-1 align-middle text-gray-600`;

  switch (currencyCode) {
    case "EUR":
      return <Euro className={iconClass} />;
    case "USD":
      return <DollarSign className={iconClass} />;
    case "GBP":
      return <PoundSterling className={iconClass} />;
    default:
      return null;
  }
};

export const isAuthenticated = () => {
  // 1. Initial check for a user object or an authentication state.
  // This is a quick and efficient way to fail early if no user is logged in.
  const currentUser = getUser();
  if (!currentUser) {
    return false;
  }

  try {
    const token = sessionStorage.getItem(currentUser.id);
    if (!token) {
      return false;
    }

    const decodedToken = jwtDecode(token, { header: false });
    const currentTime = Date.now() / 1000;

    // 2. Security-focused checks on the token itself.

    // Check A: Expiration
    if (decodedToken.exp < currentTime) {
      return false; // Token is expired
    }

    // Check B: Audience and Issuer (Ensures the token is for this app and from the correct server)
    const expectedIssuer = import.meta.env.VITE_AUTH_ISSUER.toLowerCase();
    const expectedAudience = import.meta.env.VITE_AUTH_AUDIENCE.toLowerCase();

    const tokenIssuer = decodedToken.iss?.toLowerCase();
    const tokenAudience = Array.isArray(decodedToken.aud)
      ? decodedToken.aud.map(a => a.toLowerCase())
      : decodedToken.aud?.toLowerCase();

    if (
      !tokenIssuer || !expectedIssuer || tokenIssuer !== expectedIssuer ||
      !tokenAudience || !expectedAudience ||
      (Array.isArray(tokenAudience)
        ? !tokenAudience.includes(expectedAudience)
        : tokenAudience !== expectedAudience)
    ) {
      return false;
    }

    return true; // All checks passed, the user is considered authenticated
  } catch (error) {
    // Handle cases where the token is malformed or invalid
    console.error('Token validation failed:', error);
    return false;
  }
};

export const getAccessToken = () => {
  const currentUser = getUser();
  if (currentUser !== null && currentUser !== undefined) {
    return sessionStorage.getItem(currentUser.id);
  }
  return '';
};

export const getUser = () => {
  return JSON.parse(window.localStorage.getItem('currentUser'));
};

export const getUserId = () => {
  return JSON.parse(window.localStorage.getItem('currentUser')).id;
};

export const getAdminPracticeId = () => {
  return JSON.parse(window.localStorage.getItem('currentUser')).practiceId;
};

export const getStudioURL = () => {
  return localStorage.getItem("studioURL");
};

interface RequestOptions {
  method: string;
  headers: {
    'Authorization': string;
    'Content-Type'?: string;
  };
  body?: string;
}

export const getRequestOptions = (
  requestMethod: string = 'GET',
  bodyContent: string | null | undefined = null,
  format: string = 'json'
): RequestOptions => {
  const requestOptions: RequestOptions = {
    method: requestMethod,
    headers: {
      'Authorization': 'Bearer ' + getAccessToken(),
    },
  };

  // Set content type if the format is json or empty
  if (format === '' || format === 'json') {
    requestOptions.headers['Content-Type'] = 'application/json';
  }

  // Add body if it's not GET and bodyContent is provided
  if (requestMethod !== 'GET' && bodyContent !== null && bodyContent !== undefined) {
    requestOptions.body = bodyContent;
  }

  return requestOptions;
};

export const onClientLoginSuccess = (token) => {
  const user = jwtDecode(token, { header: false }) as {
    sub: string;
    given_name: string;
    family_name: string;
    email: string;
  };

  const client = {
    id: user.sub,
    firstName: user.given_name,
    lastName: user.family_name,
    email: user.email,
  }

  const userData = {
    id: user.sub,
    email: client.email,
    firstName: client.firstName,
    lastName: client.lastName,
    token: token,
  };

  window.localStorage.setItem('currentUser', JSON.stringify(client));
  window.sessionStorage.setItem(user.sub, token);
  trackUserLogin(user.email);
  return userData;
}

export const setCurrentStudio = (studio) => {
  window.localStorage.setItem('currentStudio', JSON.stringify(studio));
  window.dispatchEvent(new Event("studioUpdated"));
};

export const getCurrentStudio = () => {
  return JSON.parse(window.localStorage.getItem('currentStudio'));
};

export const me = () => {
  const currentUser = getUser();
  if (!currentUser?.id) {
    return null;
  }
  const token = window.sessionStorage.getItem(currentUser.id);
  if (!token) {
    return null;
  }

  // 1. Explicitly type 'roles' as a string to match the incoming JWT payload
  const decodedUserToken = jwtDecode(token, { header: false }) as {
    sub: string;
    given_name: string;
    family_name: string;
    email: string;
    roles: string;
  };

  // 2. Safely parse the roles string back into an object array
  let roleObjects: Array<{ id: string }> = [];
  if (decodedUserToken.roles) {
    try {
      roleObjects = JSON.parse(decodedUserToken.roles);
    } catch (e) {
      console.error("Failed to parse roles from token", e);
    }
  }

  // 3. Map the array of objects to check for the admin ID string
  const isAdmin = roleObjects.some(role => role.id === '007bb968-9766-495b-9155-08260d984581');

  return {
    id: decodedUserToken.sub,
    firstName: decodedUserToken.given_name,
    lastName: decodedUserToken.family_name,
    email: decodedUserToken.email,
    isAdmin: isAdmin,
  };
};
// const appInsights = new ApplicationInsights({
//   config: {
//     instrumentationKey: '0235da7b-f82d-4f4a-9118-044a4b65b30d', // Replace with your key from Azure
//     enableAutoRouteTracking: true, // Automatically tracks route changes in SPAs
//     enableAjaxErrorStatusText: true, // Captures error details for API calls
//   },
// });

// Initialize Application Insights
//appInsights.loadAppInsights();

export const trackUserLogin = (email) => {
  // appInsights.setAuthenticatedUserContext(email);
  // appInsights.trackEvent({
  //   name: 'UserLogin',
  //   properties: {
  //     email: email,
  //     timestamp: new Date().toISOString(),
  //     applicationName: 'FitCalendar'
  //   },
  // });

  faro.api.pushEvent('UserLogin', {
    email
  });

};

export const logException = (error: Error, severityLevel: number = 3) => {
  // if (appInsights) {
  //   appInsights.trackException({
  //     exception: error,
  //     severityLevel, // Severity level: 0 (Verbose) to 4 (Critical)
  //     properties: { applicationName: 'FitCalendar' },
  //   });
  // }
  faro.api.pushError(error);
};